/**
 * Store Module
 * Manages store information, locations, categories, and products
 * Contains all business logic for store operations (SOLID: Single Responsibility)
 */

import {
  fetchStoreInfo,
  fetchStoreLocations,
  fetchStoreConfig,
  fetchPromotions
} from "@/services/storeService";

import {
  transformStoreInfo,
  transformStoreLocations,
  transformStoreConfig,
  createStoreError
} from "@/transformers/storeTransformer";
import {
  saveLastUsedAlias,
  loadSelectedLocation,
  saveDiningOption,
  saveOrderConfig
} from "@/services/localStorageService";
import { getOrderTypeFromDiningOption } from "@/composables/usePersistedOrderSettings";
import Vue from "vue";
import moment from "moment-timezone";
import {
  getCurrencySymbolFromStore,
  isEuroCurrency,
  formatPrice as formatPriceUtil
} from "@/utils/formatters";
import liveObjectsService from "@/helpers/liveObjectsService";
import {
  getAblyApiKey,
  extractSubdomainFromBaseUrl
} from "@/helpers/realtimeConfig";

/**
 * Maps order type strings to dining option name patterns
 * This allows flexible matching between UI types and backend dining options
 */
const ORDER_TYPE_TO_DINING_OPTION_PATTERNS = {
  pickup: ["pickup", "pick-up", "pick_up", "takeout", "take-out", "take_out"],
  curbside: [
    "curbside",
    "curb-side",
    "curb_side",
    "curb pickup",
    "curb-pickup"
  ],
  delivery: ["delivery", "deliver"]
};

export const storeModule = {
  namespaced: true,
  state: {
    // Main store info
    store: {},
    storeName: "",
    storeAlias: "",
    baseUrl: null,
    timezone: "",

    // Store locations (sucursales)
    stores: [],

    // Menu data
    categories: [],
    products: [],
    promotions: [],
    productIds86d: {},
    product86dUnsubscribes: [],

    // Store settings
    isStoreTaxInclusive: false,
    tax_round: "",
    isStoreClosed: false,
    availableDiningOptions: [],
    availablePaymentMethods: [],
    dining_option: null,
    diningOptionName: "",
    diningOptionObject: null
  },
  getters: {
    // Store info
    getStore: (state) => state.store,
    getStoreName: (state) => state.storeName,
    getStoreAlias: (state) => state.storeAlias,
    getStoreAddressFields: (state) => state.store?.address_fields,
    getCurrencySymbol: (state) =>
      getCurrencySymbolFromStore(state.store?.currency) || "$",
    /** Formats a price for display (Euro format for EUR: e.g. "12,50 €"). */
    formatPrice: (state, getters) => (value) => {
      const symbol = getters.getCurrencySymbol;
      const isEur = isEuroCurrency(state.store?.currency);
      return formatPriceUtil(value, symbol, isEur);
    },
    baseUrl: (state) => state.baseUrl,
    getStoreTimezone: (state) => state.timezone || "UTC",
    storeCountry: (state) => state.store?.country,

    // Store locations
    getStores: (state) => state.stores,

    // Menu
    getCategories: (state) => state.categories,
    getProducts: (state) => state.products,
    getPromotions: (state) => state.promotions,
    isProduct86d: (state) => (productId) =>
      !!state.productIds86d[String(productId)],
    filteredMenu: (state, getters, rootState, rootGetters) => {
      // Use products from backend only
      const categories = state.categories || [];
      const searchQuery =
        rootState.app?.searchQuery || rootGetters["app/getSearchQuery"] || "";

      if (!state.products || state.products.length === 0) {
        return [];
      }

      if (!searchQuery.trim()) {
        return categories.map((cat) => ({
          ...cat,
          items: state.products.filter((p) => p.categories.includes(cat.id))
        }));
      }

      return categories
        .map((cat) => ({
          ...cat,
          items: state.products.filter(
            (p) =>
              p.categories.includes(cat.id) &&
              (p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (p.description &&
                  p.description
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase())))
          )
        }))
        .filter((cat) => cat.items.length > 0);
    },

    // Store settings
    isStoreTaxInclusive: (state) => state.isStoreTaxInclusive,
    getTaxRound: (state) => state.tax_round,
    getIsStoreClosed: (state) => state.isStoreClosed,
    availableDiningOptions: (state) => state.availableDiningOptions,
    availablePaymentMethods: (state) => state.availablePaymentMethods,
    dining_option: (state) => state.dining_option,
    getDiningOptionName: (state) => state.diningOptionName,
    getDiningOptionObject: (state) => state.diningOptionObject,
    getDeliveryFee: (state) => {
      // Get delivery fee from diningOptionObject if available
      // Default to 0 if not available or if not delivery option
      const diningOption = state.diningOptionObject;
      if (diningOption && diningOption.delivery_fee !== undefined) {
        return diningOption.delivery_fee;
      }
      // If it's a delivery option but no fee specified, default to 0
      return 0;
    },
    isScheduleEnabled: (state) => {
      const option = state.diningOptionObject;
      return option && option.is_scheduling_enabled === true;
    }
  },
  mutations: {
    // Store info
    SET_STORE(state, payload) {
      state.store = payload;
    },
    SET_STORE_NAME(state, payload) {
      state.storeName = payload;
    },
    SET_STORE_ALIAS(state, payload) {
      state.storeAlias = payload;
    },
    SET_BASEURL(state, baseUrl) {
      state.baseUrl = baseUrl;
    },
    SET_STORE_TIMEZONE(state, payload) {
      state.timezone = payload;
    },

    // Store locations
    SET_STORES(state, stores) {
      state.stores = stores;
    },

    // Menu
    SET_CATEGORIES(state, payload) {
      state.categories = payload;
    },
    SET_PRODUCTS(state, payload) {
      state.products = payload;
    },
    SET_PRODUCT_IDS_86D(state, payload) {
      state.productIds86d = payload ? { ...payload } : {};
    },
    SET_PRODUCT_86D(state, { productId, value }) {
      const id = String(productId);
      if (value) {
        Vue.set(state.productIds86d, id, true);
      } else {
        Vue.delete(state.productIds86d, id);
      }
    },
    ADD_PRODUCT_86D_UNSUBSCRIBE(state, unsubscribe) {
      if (typeof unsubscribe === "function") {
        state.product86dUnsubscribes.push(unsubscribe);
      }
    },
    CLEAR_PRODUCT_86D_UNSUBSCRIBES(state) {
      state.product86dUnsubscribes.forEach((unsubscribe) => {
        try {
          unsubscribe();
        } catch (e) {
          // Ignore cleanup failures.
        }
      });
      state.product86dUnsubscribes = [];
    },
    SET_PROMOTIONS(state, payload) {
      state.promotions = Array.isArray(payload) ? payload : [];
    },

    // Store settings
    SET_STORE_TAX_INCLUSIVE(state, isTaxInclusive) {
      if (isTaxInclusive === 2 || isTaxInclusive === "inclusive") {
        state.isStoreTaxInclusive = true;
      } else {
        state.isStoreTaxInclusive = false;
      }
    },
    SET_TAX_ROUND(state, payload) {
      state.tax_round = payload;
    },
    setIsStoreClosed(state, payload) {
      state.isStoreClosed = payload;
    },
    setAvailableDiningOptions(state, payload) {
      state.availableDiningOptions = payload;
    },
    setAvailablePaymentMethods(state, payload) {
      state.availablePaymentMethods = payload;
    },
    setDiningOption(state, payload) {
      state.dining_option = payload;
      // Find the complete dining option object from availableDiningOptions
      // This ensures all properties are preserved (same as root)
      const diningOptionObject = state.availableDiningOptions.find(
        (d) => d.id === payload
      );
      // Assign the complete object directly (same as root)
      // The object from availableDiningOptions contains all properties:
      // id, name, description, is_deliverable, is_scheduling_enabled,
      // timings, delivery_fee, minimum_order_requirements, etc.
      state.diningOptionObject = diningOptionObject || null;
      state.diningOptionName = diningOptionObject
        ? diningOptionObject.name
        : "";
    }
  },
  actions: {
    // ============================================
    // SIMPLE ACTIONS (Delegation to mutations)
    // ============================================
    setStore({ commit }, payload) {
      commit("SET_STORE", payload);
    },
    setStoreName({ commit }, payload) {
      commit("SET_STORE_NAME", payload);
    },
    setStoreAlias({ commit }, payload) {
      commit("SET_STORE_ALIAS", payload);
    },
    setBaseUrl({ commit }, baseUrl) {
      commit("SET_BASEURL", baseUrl);
    },
    setStoreTimezone({ commit }, payload) {
      commit("SET_STORE_TIMEZONE", payload);
    },
    setStores({ commit }, payload) {
      commit("SET_STORES", payload);
    },
    setCategories({ commit }, payload) {
      commit("SET_CATEGORIES", payload);
    },
    setProducts({ commit }, payload) {
      commit("SET_PRODUCTS", payload);
      commit(
        "SET_PRODUCT_IDS_86D",
        (Array.isArray(payload) ? payload : []).reduce((ids, product) => {
          if (product && product.is_86d) {
            ids[String(product.id)] = true;
          }
          return ids;
        }, {})
      );
    },
    setPromotions({ commit }, payload) {
      commit("SET_PROMOTIONS", payload);
    },
    setTaxRound({ commit }, payload) {
      commit("SET_TAX_ROUND", payload);
    },
    isStoreTaxInclusive({ commit }, payload) {
      commit("SET_STORE_TAX_INCLUSIVE", payload);
    },
    setDiningOption({ commit }, diningOptionId) {
      commit("setDiningOption", diningOptionId);
    },

    // ============================================
    // BUSINESS LOGIC ACTIONS
    // ============================================

    /**
     * Loads store information by alias
     * @param {Object} context - Vuex context
     * @param {string} storeAlias - Store alias identifier
     * @returns {Promise<Object>} Transformed store info
     */
    async loadStoreInfo({ commit, state }, storeAlias) {
      if (!storeAlias) {
        throw new Error("Store alias is required");
      }

      try {
        const response = await fetchStoreInfo(storeAlias);

        if (!response.ok) {
          const errorData = createStoreError(response.status);
          throw errorData;
        }

        const rawData = await response.json();
        const transformedData = transformStoreInfo(rawData);

        // Update store state - merge with existing store to preserve other fields (like tax_rates)
        // Explicitly preserve tax_rates to ensure they are not overwritten by undefined/null in transformedData
        const currentTaxRates = state.store?.tax_rates;
        const newStoreState = { ...state.store, ...transformedData };

        if (currentTaxRates) {
          newStoreState.tax_rates = currentTaxRates;
        }

        commit("SET_STORE", newStoreState);
        commit("SET_STORE_NAME", transformedData.name || "");
        commit("SET_STORE_ALIAS", storeAlias);
        commit("SET_BASEURL", transformedData.dashboardLink);

        return transformedData;
      } catch (error) {
        console.error("Failed to load store info:", error);
        throw error;
      }
    },

    /**
     * Loads store locations from dashboard
     * @param {Object} context - Vuex context
     * @param {string} dashboardLink - Dashboard base URL
     * @returns {Promise<Array>} Transformed locations array
     */
    async loadStoreLocations({ commit }, dashboardLink) {
      if (!dashboardLink) {
        throw new Error("Dashboard link is required");
      }

      try {
        const response = await fetchStoreLocations(dashboardLink);

        if (!response.ok) {
          const errorData = createStoreError(response.status);
          throw errorData;
        }

        const rawData = await response.json();
        const transformedData = transformStoreLocations(rawData);

        // Update store state
        commit("SET_STORES", transformedData);

        return transformedData;
      } catch (error) {
        console.error("Failed to load store locations:", error);
        throw error;
      }
    },

    /**
     * Loads active promotions from dashboard.
     * @param {Object} context - Vuex context
     * @param {string} dashboardLink - Dashboard base URL
     * @returns {Promise<Array>} Active promotions
     */
    async loadPromotions({ commit, dispatch }, dashboardLink) {
      if (!dashboardLink) {
        commit("SET_PROMOTIONS", []);
        return [];
      }

      try {
        const response = await fetchPromotions(dashboardLink);
        if (!response.ok) {
          commit("SET_PROMOTIONS", []);
          return [];
        }

        const payload = await response.json();
        const promotions = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.results)
          ? payload.results
          : [];

        commit("SET_PROMOTIONS", promotions);
        await dispatch("order/applyPromotions", null, { root: true });
        await dispatch("order/recalculate", null, { root: true });
        return promotions;
      } catch (error) {
        console.warn("Failed to load promotions:", error);
        commit("SET_PROMOTIONS", []);
        return [];
      }
    },

    /**
     * Loads saved location from localStorage
     * @param {Object} context - Vuex context
     * @param {string} storeAlias - Store alias
     * @returns {Promise<Object|null>} Saved location or null
     */
    async loadSavedLocation(context, storeAlias) {
      try {
        return loadSelectedLocation(storeAlias);
      } catch (error) {
        console.error("Error loading saved location:", error);
        return null;
      }
    },

    /**
     * Selects initial location (on app load)
     * Business logic: Load saved location, validate it exists, fallback to first
     * @param {Object} context - Vuex context
     * @param {Object} params - { locations, storeAlias }
     * @returns {Promise<Object>} Selected location
     */
    async selectInitialLocation(
      { dispatch, commit },
      { locations, storeAlias }
    ) {
      if (!locations || locations.length === 0) {
        throw new Error("No locations available");
      }

      // 1. Try to load saved location
      const savedLocation = await dispatch("loadSavedLocation", storeAlias);

      // 2. Validate saved location exists in current locations
      let locationToSelect = null;
      if (savedLocation && savedLocation.id) {
        const locationExists = locations.find(
          (loc) => loc.id === savedLocation.id
        );
        if (locationExists) {
          locationToSelect = locationExists;
        }
      }

      // 3. Fallback to first location
      if (!locationToSelect) {
        locationToSelect = locations[0];
      }

      // 4. Set location (this action saves to localStorage)
      // Always save to localStorage, even if it's the first location (for persistence on refresh)
      await dispatch("app/setSelectedLocation", locationToSelect, {
        root: true
      });

      // 5. Clear previous menu data
      commit("SET_CATEGORIES", []);
      commit("SET_PRODUCTS", []);

      // 6. Decide if should show location modal
      // Only show modal if there are multiple locations and no saved location was found
      const shouldShowModal = !savedLocation && locations.length > 1;
      if (shouldShowModal) {
        await dispatch("app/setLocationModalOpen", true, { root: true });
      }

      return locationToSelect;
    },

    /**
     * Changes store location
     * Business logic: Check if cart has items, return confirmation flag
     * @param {Object} context - Vuex context
     * @param {Object} params - { newLocation, storeAlias }
     * @returns {Promise<Object>} { requiresConfirmation: boolean, newLocation }
     */
    async changeLocation({ rootState, dispatch }, { newLocation }) {
      const cartItems = rootState.order?.cartItems || [];
      const hasItems = cartItems.length > 0;

      if (hasItems) {
        return { requiresConfirmation: true, newLocation };
      }

      // No items, proceed directly
      await dispatch("executeLocationChange", { newLocation });
      return { requiresConfirmation: false };
    },

    /**
     * Executes location change (after confirmation or if no items)
     * Business logic: Clear cart, clear menu, set location, load config
     * @param {Object} context - Vuex context
     * @param {Object} params - { newLocation }
     */
    async executeLocationChange(
      { dispatch, commit, rootGetters },
      { newLocation }
    ) {
      // 1. Clear cart and IndexDB
      await dispatch("order/clearCart", null, { root: true });

      // 2. Clear menu data
      commit("SET_CATEGORIES", []);
      commit("SET_PRODUCTS", []);

      // 3. Set new location (saves to localStorage)
      await dispatch("app/setSelectedLocation", newLocation, { root: true });

      // 4. Load new store config
      const baseUrl = rootGetters.baseUrl;
      if (baseUrl && newLocation?.id) {
        await dispatch("loadPromotions", baseUrl);
        await dispatch("loadStoreConfig", { baseUrl, storeId: newLocation.id });

        // 5. After loading config, select the first available dining option
        // This will automatically save it to localStorage for persistence
        await dispatch("setFirstAvailableDiningOption");

        // 6. After loading config, check if store is in schedule-only mode
        // and set default scheduledDay and scheduledTime if needed
        await dispatch("setDefaultScheduleIfNeeded", null, { root: true });
      }

      // 7. Close location modal
      await dispatch("app/setLocationModalOpen", false, { root: true });
    },

    /**
     * Loads store configuration and applies it
     * Business logic: Fetch config, transform, apply to store, load menu
     * @param {Object} context - Vuex context
     * @param {Object} params - { baseUrl, storeId } or just storeId (uses rootGetters.baseUrl)
     */
    async loadStoreConfig(
      { commit, dispatch, rootGetters, rootState },
      params
    ) {
      let baseUrl, storeId;

      if (typeof params === "object") {
        baseUrl = params.baseUrl;
        storeId = params.storeId;
      } else {
        baseUrl = rootGetters.baseUrl;
        storeId = params;
      }

      if (!baseUrl || !storeId) {
        throw new Error("Base URL and Store ID are required");
      }

      try {
        // Fetch config
        const response = await fetchStoreConfig(baseUrl, storeId);

        if (!response.ok) {
          const errorData = createStoreError(response.status);
          throw errorData;
        }

        const rawData = await response.json();

        // Validate dining options
        if (!rawData.dining_options || rawData.dining_options.length === 0) {
          throw createStoreError(400, "No dining options available");
        }

        // Transform config
        const transformedData = transformStoreConfig(rawData);
        const storeData = transformedData.store || {};
        const diningOptions = transformedData.diningOptions || [];
        const paymentTypes = transformedData.paymentTypes || [];
        const categories = transformedData.categories || [];
        const products = transformedData.products || [];
        const taxOption = storeData.taxOption ?? storeData.tax_option ?? 0;
        const taxRound = storeData.taxRound ?? storeData.tax_round ?? "";
        const timezone = storeData.timezone || "UTC";

        // Apply config to store (multiple commits)
        commit("SET_STORE_TAX_INCLUSIVE", taxOption);
        commit("SET_TAX_ROUND", taxRound);
        commit("setAvailableDiningOptions", diningOptions);
        commit("setAvailablePaymentMethods", paymentTypes);
        commit("SET_STORE_TIMEZONE", timezone);

        // Set default dining option if not set - use setFirstAvailableDiningOption
        // to ensure proper filtering and schedule setup
        if (!rootState.store.dining_option && diningOptions.length > 0) {
          // Use setFirstAvailableDiningOption to get filtered available options
          // and set the first one with proper schedule configuration
          await dispatch("setFirstAvailableDiningOption");
        }

        // Load menu data (categories and products)
        commit("SET_CATEGORIES", categories);
        commit("SET_PRODUCTS", products);
        await dispatch("hydrateProduct86dFromProducts", products);

        // Update store object with additional data
        if (storeData) {
          commit("SET_STORE", { ...rootState.store.store, ...storeData });
          if (storeData.name) {
            commit("SET_STORE_NAME", storeData.name);
          }
        }

        dispatch("initializeProduct86dRealtime", { baseUrl, storeId }).catch(
          (error) => {
            console.warn("[products86d] realtime unavailable", error);
          }
        );

        return transformedData;
      } catch (error) {
        console.error("Failed to load store config:", error);
        throw error;
      }
    },

    hydrateProduct86dFromProducts({ commit }, products) {
      const productIds = {};
      (Array.isArray(products) ? products : []).forEach((product) => {
        if (product && product.is_86d) {
          productIds[String(product.id)] = true;
        }
      });
      commit("SET_PRODUCT_IDS_86D", productIds);
    },

    async initializeProduct86dRealtime(
      { commit, state },
      { baseUrl, storeId }
    ) {
      commit("CLEAR_PRODUCT_86D_UNSUBSCRIBES");
      const apiKey = getAblyApiKey();
      if (!apiKey || !storeId || !baseUrl) return;

      await liveObjectsService.initialize({
        apiKey,
        storeId,
        storeSubdomain: extractSubdomainFromBaseUrl(baseUrl),
        clientId: `biyo-order-product-86-${storeId}-${Date.now()}`
      });

      const snapshot = await liveObjectsService.getAllProduct86dSnapshot();
      const productIds = { ...state.productIds86d };
      snapshot.forEach((row) => {
        if (
          row &&
          row.product_id != null &&
          row.modifier_option_id == null &&
          row.is_86d
        ) {
          productIds[String(row.product_id)] = true;
        }
      });
      commit("SET_PRODUCT_IDS_86D", productIds);

      const unsubscribeSet = await liveObjectsService.subscribeToProduct86dSet(
        ({ data }) => {
          if (!data || data.modifier_option_id != null) return;
          commit("SET_PRODUCT_86D", {
            productId: data.product_id,
            value: !!data.is_86d
          });
        }
      );
      const unsubscribeRemove = await liveObjectsService.subscribeToProduct86dRemove(
        ({ data }) => {
          if (!data || data.modifier_option_id != null) return;
          commit("SET_PRODUCT_86D", {
            productId: data.product_id,
            value: false
          });
        }
      );

      commit("ADD_PRODUCT_86D_UNSUBSCRIBE", unsubscribeSet);
      commit("ADD_PRODUCT_86D_UNSUBSCRIBE", unsubscribeRemove);
    },

    /**
     * Initializes store completely (orchestrates full initialization)
     * Business logic: Set alias, load info, load locations, select location, load config, init cart
     * @param {Object} context - Vuex context
     * @param {Object} params - { storeAlias }
     */
    async initializeStore({ dispatch, commit }, { storeAlias }) {
      if (!storeAlias) {
        throw new Error("Store alias is required");
      }

      try {
        // Set fetchingData to true BEFORE starting any API calls
        // This ensures skeleton is shown immediately, even before API requests
        await dispatch("app/setFetchingData", true, { root: true });

        // 1. Set store alias
        await dispatch("setStoreAlias", storeAlias);
        commit("order/setTipAmount", 0, { root: true });
        await dispatch("app/toggleConfigLoader", null, { root: true });

        // 2. Load store info
        const storeInfo = await dispatch("loadStoreInfo", storeAlias);

        // Save last used alias
        saveLastUsedAlias(storeAlias);

        // Set base URL
        await dispatch("setBaseUrl", storeInfo.dashboardLink);

        // 3. Load active promotions from dashboard
        await dispatch("loadPromotions", storeInfo.dashboardLink);

        // 4. Load store locations
        const locations = await dispatch(
          "loadStoreLocations",
          storeInfo.dashboardLink
        );

        // 5. Select initial location (if locations available)
        if (locations && locations.length > 0) {
          const selectedLocation = await dispatch("selectInitialLocation", {
            locations,
            storeAlias
          });

          // 6. Load store config (if location selected)
          if (selectedLocation && selectedLocation.id) {
            await dispatch("loadStoreConfig", selectedLocation.id);
          }
        }

        // 7. Initialize IndexDB and restore cart
        await dispatch("order/initializeCart", null, { root: true });

        return { storeInfo, locations };
      } catch (error) {
        console.error("Failed to initialize store:", error);
        throw error;
      } finally {
        await dispatch("app/setFetchingData", false, { root: true });
      }
    },

    /**
     * Finds a dining option by order type
     * Business logic: Match order type to dining option using flexible patterns
     * @param {Object} context - Vuex context
     * @param {string} orderType - Order type ('pickup', 'curbside', 'delivery')
     * @returns {Object|null} Matching dining option or null
     */
    findDiningOptionByType({ state }, orderType) {
      const availableDiningOptions = state.availableDiningOptions || [];
      if (!availableDiningOptions.length) {
        return null;
      }

      const patterns = ORDER_TYPE_TO_DINING_OPTION_PATTERNS[orderType] || [];

      // First, try exact match by name (case insensitive)
      const exactMatch = availableDiningOptions.find((option) => {
        const name = (option.name || "").toLowerCase();
        return patterns.some((pattern) => name === pattern);
      });

      if (exactMatch) {
        return exactMatch;
      }

      // Then, try partial match
      const partialMatch = availableDiningOptions.find((option) => {
        const name = (option.name || "").toLowerCase();
        return patterns.some((pattern) => name.includes(pattern));
      });

      if (partialMatch) {
        return partialMatch;
      }

      // Fallback: return first available option
      return availableDiningOptions[0] || null;
    },

    /**
     * Sets dining option by order type
     * Business logic: Find matching dining option and set it
     * @param {Object} context - Vuex context
     * @param {string} orderType - Order type ('pickup', 'curbside', 'delivery')
     * @returns {Promise<Object>} { success: boolean, option: Object|null }
     */
    async setDiningOptionByType({ dispatch }, orderType) {
      if (!orderType) {
        console.warn("setDiningOptionByType: Invalid order type");
        return { success: false, option: null };
      }

      const option = await dispatch("findDiningOptionByType", orderType);
      if (!option) {
        return { success: false, option: null };
      }

      await dispatch("setDiningOption", option.id);

      return { success: true, option };
    },

    /**
     * Gets the first available day and time for a dining option with scheduling enabled
     * @param {Object} context - Vuex context (unused but required for action signature)
     * @param {Object} params - { diningOption, timezone }
     * @returns {Object|null} { scheduledDay, scheduledTime } or null if no available day/time
     */
    getFirstAvailableSchedule(context, { diningOption, timezone }) {
      if (!diningOption || !diningOption.timings || !timezone) {
        return null;
      }

      const now = moment().tz(timezone);
      const allTimings = diningOption.timings.filter(
        (timing) => !timing.is_closed
      );

      if (allTimings.length === 0) {
        return null;
      }

      // Find first available day (next 7 days)
      for (let i = 0; i < 7; i++) {
        const checkDate = now.clone().add(i, "days");
        const momentDay = checkDate.day();
        const dayOfWeek = momentDay === 0 ? 7 : momentDay;

        const timingsForDay = allTimings.filter(
          (timing) => timing.day_of_week === dayOfWeek
        );

        if (timingsForDay.length === 0) {
          continue;
        }

        // Generate time slots for this day
        const timeSlots = [];
        timingsForDay.forEach((timing) => {
          const dayStart = checkDate.clone().startOf("day");
          const openDuration = moment.duration(timing.opening_time);
          const closeDuration = moment.duration(timing.closing_time);

          const openTime = dayStart.clone().add(openDuration);
          const closeTime = dayStart.clone().add(closeDuration);

          if (closeTime.isBefore(openTime) || closeTime.isSame(openTime)) {
            closeTime.add(1, "day");
          }

          let currentSlot = openTime.clone();
          while (currentSlot.isBefore(closeTime)) {
            const timeString = currentSlot.format("h:mm A");
            if (!timeSlots.includes(timeString)) {
              timeSlots.push(timeString);
            }
            currentSlot = currentSlot.add(30, "minutes");
          }
        });

        // Sort time slots
        const sortedTimeSlots = timeSlots.sort((a, b) => {
          const timeA = moment(a, "h:mm A");
          const timeB = moment(b, "h:mm A");
          return timeA.diff(timeB);
        });

        // Filter out past times for today
        let availableTimeSlots = sortedTimeSlots;
        if (i === 0) {
          const currentTime = now.clone();
          const bufferMinutes = 30;
          availableTimeSlots = sortedTimeSlots.filter((timeString) => {
            const slotTime = moment(timeString, "h:mm A");
            slotTime.set({
              year: currentTime.year(),
              month: currentTime.month(),
              date: currentTime.date()
            });
            return slotTime.isAfter(
              currentTime.clone().add(bufferMinutes, "minutes")
            );
          });
        }

        if (availableTimeSlots.length > 0) {
          // Determine day label
          let scheduledDay = "";
          if (i === 0) {
            scheduledDay = "Today";
          } else if (i === 1) {
            scheduledDay = "Tomorrow";
          } else {
            scheduledDay = checkDate.format("YYYY-MM-DD");
          }

          return {
            scheduledDay: scheduledDay,
            scheduledTime: availableTimeSlots[0]
          };
        }
      }

      return null;
    },

    /**
     * Sets the first available dining option for the current store
     * Business logic: Get filtered available dining options and set the first one
     * If the option has scheduling enabled, also sets the first available day and time
     * @param {Object} context - Vuex context with rootState and rootGetters
     * @returns {Promise<Object|null>} The selected dining option or null
     */
    async setFirstAvailableDiningOption({ dispatch, commit, rootGetters }) {
      // Use the getter from selectedStore module directly (single source of truth)
      // This getter already filters dining options based on availability and scheduling
      const availableOptions =
        rootGetters["selectedStore/getAvailableDiningOptions"] || [];

      // Validate that availableOptions is an array
      if (!Array.isArray(availableOptions) || availableOptions.length === 0) {
        console.warn(
          "setFirstAvailableDiningOption: No available dining options found after filtering",
          {
            availableOptions,
            allDiningOptions: rootGetters.availableDiningOptions || []
          }
        );
        return null;
      }

      // Ensure availableOptions is an array
      if (!Array.isArray(availableOptions) || availableOptions.length === 0) {
        console.warn(
          "setFirstAvailableDiningOption: No available dining options found after filtering",
          {
            availableOptions,
            allDiningOptions: allDiningOptions.map((opt) => ({
              id: opt.id,
              name: opt.name
            }))
          }
        );
        return null;
      }

      // When changing location, always select the first available dining option
      // (don't try to load saved option from previous location)
      const selectedOption = availableOptions[0];

      if (!selectedOption || !selectedOption.id) {
        console.warn("setFirstAvailableDiningOption: Invalid selected option", {
          selectedOption
        });
        return null;
      }

      // Set the dining option
      commit("setDiningOption", selectedOption.id);

      // Save dining option to localStorage for persistence (always save, even if it was already saved)
      saveDiningOption(selectedOption.id);

      // Create and save order config based on the dining option
      const scheduleEnabled = selectedOption?.is_scheduling_enabled === true;
      const orderType = getOrderTypeFromDiningOption(selectedOption.name || "");
      const timezone = rootGetters.getStoreTimezone || "UTC";

      const orderConfig = {
        type: orderType,
        timing: "asap",
        scheduledDay: undefined,
        scheduledTime: undefined
      };

      // Even when defaulting to ASAP, prefill the first available day and time
      // so switching to "scheduled" has sensible defaults ready
      if (scheduleEnabled) {
        const firstSchedule = await dispatch("getFirstAvailableSchedule", {
          diningOption: selectedOption,
          timezone: timezone
        });

        if (
          firstSchedule &&
          firstSchedule.scheduledDay &&
          firstSchedule.scheduledTime
        ) {
          orderConfig.scheduledDay = firstSchedule.scheduledDay;
          orderConfig.scheduledTime = firstSchedule.scheduledTime;
        }
      }

      // Set order config in store FIRST (root level) - this ensures it's available immediately
      commit("SET_ORDER_CONFIG", orderConfig, { root: true });

      // Save order config to localStorage for persistence
      saveOrderConfig(orderConfig);

      return selectedOption;
    }
  }
};
